import type { ControlsType, Object3D, PerspectiveCamera, SceneControl } from '@anov/3d-core'
import { Box3, EventDispatcher, Group, MathUtils, Plane, Quaternion, TWEEN, Vector3, use, utils } from '@anov/3d-core'
import FollowControls from './FollowOrbitControls'
import type { CameraExpandInterface, FollowParamsOption, vector3Array } from '.'

interface ChangeCameraPresetOptions {
  duration?: number
  onUpdate?: (params: { position: Vector3; lookat: Vector3 }) => void
  onStart?: () => void
  onComplate?: () => void
  isTrigger?: boolean
}

/**
 * CameraExpand, 主要提供了一些PerspectiveCamera的拓展能力
 */
export class PerspectiveCameraExpand extends EventDispatcher implements CameraExpandInterface {
  private _isTransitionPeriod = false
  private camera: PerspectiveCamera
  private controls: ControlsType
  private sceneControl: SceneControl
  private tween: any
  private _isCameraChange = false
  private followId = 0
  private followCancelMap: Map<number, () => void> = new Map()

  constructor(camera: PerspectiveCamera, controls: ControlsType, sceneControl: SceneControl) {
    super()

    this.camera = camera
    this.controls = controls
    this.sceneControl = sceneControl

    this.bindEvent()
  }

  get isTransitionPeriod() {
    return this._isTransitionPeriod
  }

  get isCameraChange() {
    return this._isCameraChange
  }

  private bindEvent() {
    this.controls.addEventListener('start', () => {
      this.sceneControl.context.storeManagement.set('enableEvents', false)
      this._isCameraChange = true
    })

    this.controls.addEventListener('end', () => {
      this.sceneControl.context.storeManagement.set('enableEvents', true)
      this._isCameraChange = false
    })

    this.controls.addEventListener('change', () => {
      this.dispatchEvent({
        type: 'change',
        position: this.camera.position,
        target: this.controls.target,
      })
    })
  }

  /**
   * calculate last position from position box
   * if distance is not defined, calculate distance from box size
   * @param currentPos
   * @param distanceCoefficient
   * @param distance
   * @returns
   */
  private caclCurrentPos(currentPos: vector3Array[], distanceCoefficient = 1, distance?: number) {
    const box = new Box3()

    box.setFromPoints(currentPos.map(item => new Vector3(item[0], item[1], item[2])))

    const center = box.getCenter(new Vector3())
    const size = box.getSize(new Vector3())
    const halfDiagonal = size.length() * 0.5

    const maxDistance = halfDiagonal / Math.tan(this.camera.fov / 2 * Math.PI / 180)
    const cameraToCenterDistance = maxDistance
    const directionVector = (this.camera.position.clone()).sub(this.controls.target).normalize()

    const lastPosition = directionVector.multiplyScalar(distance ?? cameraToCenterDistance * distanceCoefficient).add(center)
    const lastLookat = center

    return {
      lastPosition,
      lastLookat,
    }
  }

  /**
   * caclSingleCoordinate, when use focus and target is single point
   * @param prevTarget
   * @param prevPosition
   * @param lastTarget
   * @returns
   */
  private caclSingleCoordinate(prevTarget: Vector3, prevPosition: Vector3, lastTarget: Vector3, distance: number) {
    const directionVector = prevPosition.clone().sub(prevTarget).normalize()
    const lastPosition = directionVector.multiplyScalar(distance).add(lastTarget)

    return lastPosition
  }

  /**
   * camera focus
   * @param params
   * @returns
   */
  public focus(params: {
    target: vector3Array | vector3Array[] | string
    pitch?: number
    yaw?: number
    distance?: number // distance from target
    duration?: number
    isTrigger?: boolean
    distanceCoefficient?: number
    onUpdate?: (args: { position: Vector3; target: Vector3 }) => void
  }) {
    return new Promise<
            {
              position: number[]
              target: number[]
              duration: number
              userInput: boolean
              pitch: number
            }
        >((res) => {
          const { target: position, pitch = 0, yaw = 0, distance = undefined, duration, onUpdate, distanceCoefficient = 1 } = params

          const moveOptions = () => ({
            duration: (duration && duration * 1000) ?? 1000,
            isTrigger: params.isTrigger ?? true,
            onUpdate: ({ position, lookat }) => {
              onUpdate && onUpdate({ position, target: lookat })
            },
            onComplate: () => {
              res({
                position: this.camera.position.toArray(),
                target: this.controls.target.toArray(),
                duration: params.duration ?? 0,
                userInput: true,
                pitch: pitch ?? 40,
              })
            },
          }) as ChangeCameraPresetOptions

          // if target is multiple points
          if (Array.isArray(position[0])) {
            const currentPos = position as vector3Array[]
            const { lastPosition, lastLookat } = this.caclCurrentPos(currentPos, distanceCoefficient, distance)

            this.motion(
              lastPosition,
              lastLookat,
              pitch,
              yaw,
              moveOptions(),
            )
          }
          // if target is single point
          else {
            const currentPos = position as vector3Array
            const lastPosition = this.caclSingleCoordinate(this.controls.target, this.camera.position, new Vector3().fromArray(currentPos), distance)
            const lastLookat = new Vector3().fromArray(currentPos)

            this.motion(
              lastPosition,
              lastLookat,
              pitch,
              yaw,
              moveOptions(),
            )
          }
        })
  }

  /**
   * set camera， main set position and target
   * @param params
   * @returns
   */
  public set(params: {
    position: vector3Array
    target: vector3Array
    isTrigger?: boolean
    duration?: number
    onUpdate?: (args: { position: Vector3; target: Vector3 }) => void
  }) {
    const { position, target, isTrigger, duration } = params

    return new Promise<{
      position: number[]
      target: number[]
      isTrigger: boolean
      duration: number
      userInput: boolean
    }>((res, _) => {
      this.motion(
        new Vector3(position[0], position[1], position[2]),
        new Vector3(target[0], target[1], target[2]),
        0,
        0,
        {
          isTrigger: isTrigger ?? true,
          duration: (duration && duration * 1000) ?? 1000,
          onUpdate: ({ position, lookat }) => {
            params.onUpdate && params.onUpdate({ position, target: lookat })
          },
          onComplate: () => {
            res({
              position: this.camera.position.toArray(),
              target: this.controls.target.toArray(),
              isTrigger: true,
              duration: params.duration ?? 0,
              userInput: true,
            })
          },
        })
    })
  }

  /**
   * get camera position and target
   * @returns
   */
  public get() {
    return {
      position: [this.camera.position.x, this.camera.position.y, this.camera.position.z],
      target: [this.controls.target.x, this.controls.target.y, this.controls.target.z],
    }
  }

  /**
   * get current pitch
   */
  public get pitch() {
    const targetPosition = this.controls.target
    const cameraPosition = this.camera.position

    const currentDirection = cameraPosition.clone().sub(targetPosition)
    const plane = new Plane(new Vector3(0, 1, 0), 0)

    return -(MathUtils.radToDeg(plane.normal.angleTo(currentDirection)) - 90)
  }

  /**
   * get current yaw
   * @returns
   */
  public get yaw() {
    return (this.camera as (PerspectiveCamera & { rotationParent: { y: number } })).rotationParent.y
  }

  /**
   * set pitch, similar to ue
   * @param deg
   */
  public setPitch(deg: number) {
    if (deg > 90)
      deg = 90
    if (deg < -90)
      deg = -90

    const direction = new Vector3(0, 0, 1)
    const quaternion = new Quaternion()

    const pitchQuat = new Quaternion()
    pitchQuat.setFromAxisAngle(new Vector3(1, 0, 0), MathUtils.degToRad(-deg))
    quaternion.multiply(pitchQuat)

    direction.applyQuaternion(quaternion)
    direction.normalize()

    const distance = this.camera.position.distanceTo(this.controls.target)
    const cameraPosition = this.controls.target.clone().add(direction.multiplyScalar(distance))
    this.camera.position.copy(cameraPosition)
  }

  /**
   * set yaw, similar to ue
   * @param deg
   */
  public setYaw(deg: number) {
    if (deg > 180)
      deg = 180
    if (deg < -180)
      deg = -180

    // @ts-ignore
    this.camera.rotationParent.y = MathUtils.degToRad(deg)
  }

  /**
   * cacl Position By Follow
   * @param pitch
   * @param yaw
   * @param distance
   * @returns
   */
  private caclPositionByFollow(pitch = 30, yaw = 0, distance = 60) {
    const axisX = new Vector3(1, 0, 0)
    const axisY = new Vector3(0, 1, 0)
    const axisZ = new Vector3(0, 0, -1)
    const position = new Vector3(0, 0, 0)

    const anglePitch = MathUtils.degToRad(pitch)
    const angleYaw = MathUtils.degToRad(yaw)

    const quaternionX = new Quaternion().setFromAxisAngle(axisX, anglePitch)
    const quaternionY = new Quaternion().setFromAxisAngle(axisY, angleYaw)

    position.addScaledVector(axisZ, distance)
    position.applyQuaternion(quaternionX)
    position.applyQuaternion(quaternionY)

    return position
  }

  /**
   * follow 3d object, not only use in camera
   * @param target
   * @param camera
   * @param option
   * @returns
   */
  public follow(target: Object3D, camera: PerspectiveCamera, option?: FollowParamsOption) {
    const followId = this.followId++
    const { relativeRotation = false, pitch = 30, yaw = 0, distance = 10 } = option || {}
    const container = new Group()

    container.rotation.copy(target.rotation)
    container.scale.copy(target.scale)

    const oldCameraParent = camera.parent

    let needUpdateRotation = true

    container.add(camera)
    target.parent.add(container)

    this.controls.enabled = false

    const followControls = new FollowControls(this.camera, this.sceneControl.renderer.domElement)
    const curPosition = this.caclPositionByFollow(pitch, yaw, distance)

    camera.position.copy(curPosition)
    followControls.radius = camera.position.distanceTo(target.position)

    followControls.addEventListener('start', () => {
      relativeRotation && (needUpdateRotation = false)
    })

    this.controls.target.copy(target.position)
    followControls.target = target.position

    const cancelMotion = use.useframe(this.sceneControl, () => {
      container.position.copy(target.position)
      container.scale.copy(target.scale)
      needUpdateRotation && container.rotation.copy(target.rotation)

      this.controls.target.copy(target.position)

      followControls.target = target.position
      followControls.update()
    })

    const cancel = () => {
      this.controls.enabled = true

      oldCameraParent.add(camera)
      target.parent.remove(container)

      cancelMotion()
      followControls.dispose()
    }

    this.followCancelMap.set(followId, cancel)

    return followId
  }

  /**
   * cancel follow
   * @param followId
   */
  public cancelFollow(followId: number) {
    const cancel = this.followCancelMap.get(followId)
    cancel && cancel()
  }

  /**
   * cancel all follow
   */
  public cancelAllFollow() {
    this.followCancelMap.forEach((cancel) => {
      cancel()
    })
  }

  /**
   * interpolation move camera
   * @param position
   * @param target
   * @param pitch
   * @param yaw
   * @param options
   * @returns
   */
  private motion(position: Vector3, target: Vector3, pitch = 0, yaw = 0, options?: ChangeCameraPresetOptions) {
    const currentPoition = this.camera.position.clone()
    const currentPositionInterpolation = new Vector3()

    if (options?.duration === 0) {
      this.camera.position.copy(position)
      this.controls.target.copy(target)

      if (pitch) {
        // @ts-ignore
        this.camera.rotationParent.x = MathUtils.degToRad(pitch)
      }

      if (yaw) {
        // @ts-ignore
        this.camera.rotationParent.y = MathUtils.degToRad(yaw)
      }

      requestAnimationFrame(() => {
        options?.onComplate && options.onComplate()
        this.sceneControl.renderer!.render(this.sceneControl.scene!, this.sceneControl.camera!)
      })

      return
    }

    if (this.tween)
      this.tween.stop()

    this.tween = new TWEEN.Tween(
      // @ts-ignore
      { t: 0, lookat: this.controls.target.clone(), p: this.camera.rotationParent.x, yaw: this.camera.rotationParent.y })
    // @ts-ignore
      .to({ t: 1, lookat: target, p: MathUtils.degToRad(pitch), y: MathUtils.degToRad(yaw) }, options?.duration || 1000)
      .onStart(() => {
        options?.onStart && options.onStart()
        this.controls.enabled = false
        this._isTransitionPeriod = true
      })
      .easing(TWEEN.Easing.Quadratic.InOut)
      .onUpdate(({ t, lookat, p }) => {
        this.controls.target.copy(lookat)

        if (pitch)
        // @ts-ignore
          this.camera.rotationParent.x = p

        if (yaw)
        // @ts-ignore
          this.camera.rotationParent.y = p

        const currentPosition = currentPositionInterpolation.lerpVectors(currentPoition, position, t)
        this.camera.position.copy(currentPosition)
        options?.onUpdate && options.onUpdate({ position: currentPosition, lookat })
      })
      .onComplete(() => {
        requestAnimationFrame(() => {
          options?.onComplate && options.onComplate()
        })

        this.controls.enabled = true

        setTimeout(() => {
          this._isTransitionPeriod = false
        })
      })
      .start()
  }
}